import {observer} from 'mobx-react-lite'
import React, {useCallback, useEffect} from 'react'
import {Lang} from 'yougile-lang'
import {Hub} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {Backend} from '@common/backend'
import {NumberInputNew} from '@components/common/base/input-new'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {useTaskInfoLogStore} from '@components/right-panel/task-info-log/task-info-log-store'
import {useDebouncedFunction, useSyncEventUpdateEffect} from '@containers/hooks'
import {ChatUtils} from '@extensions/chat-utils'
import {Gantt} from '@extensions/gantt'

interface ChatConnectedItemProps {
  fromHub: Hub
  toHub: Hub
  type: 'dependency' | 'dependent'
}

export const TaskDependencyLinkItem = observer<ChatConnectedItemProps>(
  ({fromHub, toHub, type}) => {
    const [, store] = useTaskInfoLogStore()

    const hub = type === 'dependency' ? toHub : fromHub

    const dependencies = toHub.data.dependencies ?? {}
    const originalDependency = dependencies[fromHub.id] ?? {gap: 0}
    const dependency =
      originalDependency === true ? {gap: 0} : originalDependency

    const gapEditable = PermissionsChecker.checkEditTaskStickers(
      toHub,
      'not-user'
    )

    useSyncEventUpdateEffect('current', {
      filter: [toHub.id, fromHub.id],
      deps: [toHub.id, fromHub.id],
      debounceTime: 50
    })

    const changeGap = useCallback(
      (gap: number) => {
        const dependencies = toHub.data.dependencies ?? {}
        const original = dependencies[fromHub.id] ?? {gap: 0}
        const normalized = original === true ? {gap: 0} : original

        Gantt.linkTasks(fromHub, toHub, {...normalized, gap})
      },
      [fromHub, toHub]
    )

    const [changeGapDebounced, cancelChangeGap] = useDebouncedFunction(
      changeGap,
      1500
    )

    useEffect(() => {
      cancelChangeGap()
    }, [cancelChangeGap, dependency.gap])

    return (
      <div className="chat-connected-item flex gap-8 select-none items-start">
        <div className="flex-1 flex-col">
          <div
            className="chat-connected-item-name _link"
            onClick={() => store.openChat(hub)}
          >
            {hub.title}
          </div>
          <div className="chat-connected-item-location">
            {ChatUtils.getChatLocation(hub)}
          </div>
        </div>

        {Backend.features.ganttLag && (
          <Tooltip content={Lang.msg.timeline.lagTooltip} offset={[0, 6]}>
            <div className="flex-none flex items-center gap-8 h-fit">
              <span className="text-sm-regular text-primary">
                {Lang.msg.timeline.lag}:
              </span>
              <NumberInputNew
                className="max-w-112"
                value={dependency.gap ?? 0}
                onChange={changeGapDebounced}
                min={-365}
                max={365}
                disabled={!gapEditable}
              />
            </div>
          </Tooltip>
        )}

        {store.allowConnect() && (
          <div className="flex-none flex items-center h-32">
            <span
              className="chat-connected-item-x m-0"
              title={Lang.msg.clickDisconnect}
              onClick={() => Gantt.unlinkTasks(fromHub, toHub)}
            >
              {Lang.msg.unlink}
            </span>
          </div>
        )}
      </div>
    )
  }
)
